import { createContext, ReactNode, use, useMemo, useState } from "react"

import { kebabCase } from "lodash-es"
import { humanize } from "underscore.string"

import { arrayWrap } from "~/src/lib/array"
import { PathBuilder } from "~/src/lib/iname"
import { randomAlphaNumeric } from "~/src/lib/string"

export type FieldProviderProps = {
  children?: ReactNode
  defaultErrorMessages?: string | string[]
  errorMessages?: string | string[]
  errored?: boolean
  hint?: ReactNode
  id?: string
  label?: ReactNode
  name?: string | PathBuilder
  optional?: boolean
}

export type FieldContext = {
  descendentChange?: () => void
  errorMessages: string[]
  hasErrors: boolean
  hint?: ReactNode
  inputId: string
  label?: ReactNode
  name?: string | PathBuilder
  nameOverride?: string | PathBuilder
  optional?: boolean
}

export const FieldContext = createContext<FieldContext | null>(null)

export function useFieldContext(): FieldContext | null {
  return use(FieldContext)
}

/**
 * Container for BeeKit form fields shared state.
 */
export function FieldProvider(props: FieldProviderProps) {
  const {
    children,
    defaultErrorMessages,
    errorMessages: errorMessages_,
    errored,
    hint,
    id: id_,
    label: label_,
    name: name_,
    optional = false,
  } = props

  const [savedErrorMessages, setSavedErrorMessages] = useState<string[]>(arrayWrap(defaultErrorMessages))

  const errorMessages = useMemo(
    () => (errorMessages_ == null ? savedErrorMessages : arrayWrap(errorMessages_)),
    [errorMessages_, savedErrorMessages]
  )
  const hasErrors = useMemo(() => errored ?? errorMessages.length > 0, [errored, errorMessages])
  const name = name_?.toString()
  const inputId = useMemo(
    () => id_ ?? [name && kebabCase(name), randomAlphaNumeric()].filter((x) => x != null).join("-"),
    [id_, name]
  )
  const label = useMemo(() => label_ ?? (name != null ? humanize(name) : null), [label_, name])

  return (
    <FieldContext
      value={{
        descendentChange() {
          setSavedErrorMessages([])
        },
        hasErrors,
        errorMessages,
        name,
        hint,
        label,
        optional,
        inputId,
      }}
    >
      {children}
    </FieldContext>
  )
}
